import classNames from 'classnames';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { ButtonSquare } from 'ui/Button/ButtonSquare';
import Loader from 'ui/Loader';

import { ReactComponent as ArrowLeftIcon } from 'assets/images/icons/arrow-left.svg';

import { DashboardMap } from 'modules/Dashboard/DashboardMap';
import { ObjectCard } from 'modules/Dashboard/ObjectCard';
import { ObjectSensorsTable } from 'modules/Dashboard/ObjectSensorsTable';
import { StatisticsSummary } from 'modules/Dashboard/StatisticsSummary';
import { ZoneCardsGrid } from 'modules/Dashboard/ZoneCardsGrid';

import { getAdminUserDataRequest } from 'store/Admin/Dashboard/Dashboard.action';
import { IRootStore } from 'store/Root.reducer';

export const Dashboard: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, isLoading } = useSelector((store: IRootStore) => store.admin.dashboard);

  const statistics = useMemo(
    () => [
      { label: 'Objects', value: Number(data?.meta?.objects_count || 0) },
      { label: 'Zones', value: Number(data?.meta?.zones_count || 0) },
      { label: 'Sensors', value: Number(data?.meta?.sensors_count || 0) },
    ],
    [data?.meta],
  );

  const sortedObjects = useMemo(() => {
    if (!data?.objects) return [];
    return [...data.objects].sort((a, b) => a.name.localeCompare(b.name));
  }, [data?.objects]);

  useEffect(() => {
    let timer: NodeJS.Timer | undefined = undefined;

    if (id) {
      dispatch(getAdminUserDataRequest(id));

      timer = setInterval(() => dispatch(getAdminUserDataRequest(id)), 1000 * 60 * 10);
    }

    return () => {
      clearInterval(timer);
    };
  }, [dispatch, id]);

  return (
    <div className={classNames('dashboard')}>
      <Loader isLoading={isLoading} isGlobal />

      {!isLoading && (
        <>
          <div className={classNames('dashboard__user-header', 'dashboard__block')}>
            <div className={classNames('dashboard__user-name-row')}>
              <ButtonSquare onClick={() => navigate(-1)} variant="secondary">
                <ArrowLeftIcon />
              </ButtonSquare>

              <div className={classNames('dashboard__user-info')}>
                <div className={classNames('dashboard__user-name')}>{data?.fullname || '-'}</div>
                <div className={classNames('dashboard__user-email')}>{data?.email || '-'}</div>
              </div>
            </div>

            <StatisticsSummary items={statistics} />
          </div>

          <div className={classNames('dashboard__list')}>
            {sortedObjects.map((object) => (
              <div className={classNames('dashboard__list-item')} key={object.id}>
                <ObjectCard name={object.name} />

                {object.sensors?.length > 0 && (
                  <ObjectSensorsTable sensors={object.sensors} className="dashboard__block" />
                )}

                <ZoneCardsGrid
                  zones={[...object.zones].sort((a, b) => a.name.localeCompare(b.name))}
                />
              </div>
            ))}
          </div>

          <DashboardMap objects={data?.objects || []} />
        </>
      )}
    </div>
  );
};
